import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Home, LogOut, Mail } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useState, useEffect, useCallback } from "react";
import { clearAppState } from "@/hooks/useAppStatePreservation";
import { useAuth } from "@/hooks/useAuth";
import { useAdminCheck } from "@/hooks/useAdminCheck";
import { useInstitutionAdminCheck } from "@/hooks/useInstitutionAdminCheck";
import { useOwnerCheck } from "@/hooks/useOwnerCheck";
import { useSchoolAdminCheck } from "@/hooks/useSchoolAdminCheck";
import { AdminNavSheet } from "./AdminNavSheet";

import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";


interface AppNavHeaderProps {
  /** Page title displayed in center/right of header */
  title?: string;
  /** If true, shows a confirm dialog before navigating away (for active quizzes) */
  confirmBeforeLeave?: boolean;
  /** Custom back handler override */
  onBack?: () => void;
  /** Hide back button (e.g. on dashboard itself) */
  hideBack?: boolean;
  /** Hide home button */
  hideHome?: boolean;
  /** Hide messages icon (e.g. practice/quiz/study) */
  hideMessages?: boolean;
  /** Hide logout icon (e.g. practice/quiz/study) */
  hideLogout?: boolean;
}

export const AppNavHeader = ({
  title,
  confirmBeforeLeave = false,
  onBack,
  hideBack = false,
  hideHome = false,
  hideMessages = false,
  hideLogout = false,
}: AppNavHeaderProps) => {

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { signOut, user } = useAuth();
  const { isAdmin } = useAdminCheck();
  const { isInstitutionAdmin } = useInstitutionAdminCheck();
  const { isOwner } = useOwnerCheck();
  const { isSchoolAdmin } = useSchoolAdminCheck();
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const isAdminRoute =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/institution") ||
    location.pathname.startsWith("/anatomy");

  // Fetch unread message count
  const fetchUnread = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase.rpc("get_unread_message_count", { p_user_id: user.id });
    if (data != null) setUnreadCount(data as number);
  }, [user]);

  useEffect(() => {
    fetchUnread();
    // Poll every 60s for unread count
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  const isDashboard = location.pathname === "/dashboard";

  // Determine "home" destination based on role
  const homeRoute = (!isAdmin && isInstitutionAdmin) ? "/admin-cohorts" : "/dashboard";

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    // Use history if available, fallback to dashboard
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/dashboard", { replace: true });
    }
  };

  const handleHome = () => {
    clearAppState();
    navigate(homeRoute, { replace: true });
  };

  const guardedNavigate = (action: () => void) => {
    if (confirmBeforeLeave) {
      setPendingAction(() => action);
      setShowConfirm(true);
    } else {
      action();
    }
  };

  const onConfirm = () => {
    const action = pendingAction;
    setPendingAction(null);
    setShowConfirm(false);
    // Execute after state updates to avoid Radix dialog interference
    setTimeout(() => {
      action?.();
    }, 0);
  };

  // Don't render on dashboard (it has its own header)
  if (isDashboard) return null;

  return (
    <>
      <header className="sticky top-0 z-40 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border pt-[max(6px,calc(env(safe-area-inset-top)-22px))]" data-app-nav-header="true">
        <div className="flex items-center justify-between min-h-[48px] px-3 max-w-[430px] mx-auto">
          {/* Back button / Admin menu */}
          <div className="flex-shrink-0 min-w-[5.5rem]">
            {isAdminRoute ? (
              <AdminNavSheet />
            ) : !hideBack && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 text-muted-foreground hover:text-foreground -ml-2 min-h-[44px] min-w-[44px]"
                onClick={() => guardedNavigate(handleBack)}
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">{t("nav.back")}</span>
              </Button>
            )}
          </div>

          {/* Title */}
          {title && (
            <h2 className="text-sm font-semibold text-foreground truncate flex-1 text-center">
              {title}
            </h2>
          )}

          {/* Home + Language Toggle + Logout buttons */}
          <div className="flex-shrink-0 flex justify-end items-center gap-1">
            {/* Language selector removed from internal pages.
                Only available on Landing, Dashboard, and InstitutionDashboard. */}



            {/* Messages button */}
            {user && !hideMessages && (
              <Button
                variant="ghost"
                size="sm"
                className="relative text-muted-foreground hover:text-foreground px-2 min-h-[44px] min-w-[44px]"
                onClick={() => guardedNavigate(() => navigate("/messages"))}
                aria-label={t("messages.title")}
              >
                <Mail className="w-4 h-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-destructive text-destructive-foreground text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </Button>
            )}

            {!hideHome && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 text-muted-foreground hover:text-foreground px-2 min-h-[44px] min-w-[44px]"
                onClick={handleHome}
                style={{ pointerEvents: 'auto', zIndex: 50, position: 'relative' }}
              >
                <Home className="w-4 h-4" />
              </Button>
            )}
            {user && !hideLogout && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-destructive px-2 min-h-[44px] min-w-[44px]"
                onClick={() => guardedNavigate(async () => {
                  await signOut();
                  navigate("/auth/login", { replace: true });
                })}
                aria-label={t("nav.logout") || "Cerrar sesión"}
              >
                <LogOut className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Confirm dialog for active quiz */}
      <AlertDialog open={showConfirm} onOpenChange={(open) => { if (!open) setShowConfirm(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("nav.leaveQuizTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("nav.leaveQuizDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("nav.stayInQuiz")}</AlertDialogCancel>
            <Button variant="destructive" onClick={onConfirm}>
              {t("nav.leaveQuiz")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
